import { useState, useEffect, useContext } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/Api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY); // Clave pública

// Formulario de pago
function CheckoutForm({ token, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!stripe || !elements) {
      setMessage("Stripe no está listo");
      setLoading(false);
      return;
    }

    try {
      const card = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
      });

      if (error) {
        setMessage(error.message);
        setLoading(false);
        return;
      }

      // Crear suscripción en backend
      const res = await api.post(
        "/subscribe",
        { payment_method: paymentMethod.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Manejar pagos que requieren SCA
      const clientSecret =
        res.data.subscription?.latest_invoice?.payment_intent?.client_secret;

      if (clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(clientSecret);
        if (confirmError) {
          setMessage(confirmError.message);
          setLoading(false);
          return;
        }
      }

      setMessage("Suscripción creada con éxito");
      onSuccess();

    } catch (err) {
      setMessage(err.response?.data?.message || "Error al suscribirse");
      console.error(err.response || err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubscribe} style={{ maxWidth: 400 }}>
      <CardElement options={{ hidePostalCode: true }} />
      <button type="submit" disabled={loading} style={{ marginTop: 10 }}>
        {loading ? "Procesando..." : "Suscribirse"}
      </button>
      {message && <p style={{ marginTop: 10 }}>{message}</p>}
    </form>
  );
}

// Componente principal de suscripción
export default function Subscribe() {
  const { token } = useContext(AuthContext);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loadingCancel, setLoadingCancel] = useState(false);

  // Chequear estado de suscripción
  const checkSubscription = async () => {
    try {
      const res = await api.get("/subscribe/check", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionInfo({
        active: res.data.subscribed,
        trialEndsAt: res.data.trial_ends_at,
        endsAt: res.data.ends_at,
        status: res.data.status,
      });
    } catch (err) {
      console.error("Error al chequear suscripción:", err.response || err);
    }
  };

  // Cancelar suscripción
  const handleCancel = async () => {
    setLoadingCancel(true);
    try {
      const res = await api.post(
        "/subscribe/cancel",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message);
      checkSubscription();
    } catch (err) {
      const message =
        err.response?.data?.message || "Error desconocido al cancelar la suscripción";
      alert(message);
      console.error("Error al cancelar:", err.response || err);
    } finally {
      setLoadingCancel(false);
    }
  };

  // Reanudar suscripción
  const handleResume = async () => {
    setLoadingCancel(true);
    try {
      const res = await api.post(
        "/subscribe/resume",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message);
      checkSubscription();
    } catch (err) {
      const message =
        err.response?.data?.message || "Error desconocido al reanudar la suscripción";
      alert(message);
      console.error("Error al reanudar:", err.response || err);
    } finally {
      setLoadingCancel(false);
    }
  };

  useEffect(() => {
    if (token) checkSubscription();
  }, [token]);

  return (
    <div>
      {subscriptionInfo?.active ? (
        <div>
          <p>Suscripción activa</p>
          {subscriptionInfo.trialEndsAt && (
            <p>Trial termina: {new Date(subscriptionInfo.trialEndsAt).toLocaleDateString()}</p>
          )}
          {subscriptionInfo.endsAt && (
            <p>Fin de suscripción: {new Date(subscriptionInfo.endsAt).toLocaleDateString()}</p>
          )}
          <button
            onClick={handleCancel}
            disabled={loadingCancel || subscriptionInfo.status === "canceled"}
          >
            {loadingCancel ? "Procesando..." : "Cancelar suscripción"}
          </button>

          {subscriptionInfo.status === "canceled" && (
            <button onClick={handleResume} disabled={loadingCancel}>
              {loadingCancel ? "Procesando..." : "Reanudar suscripción"}
            </button>
          )}
        </div>
      ) : (
        <Elements stripe={stripePromise}>
          <CheckoutForm token={token} onSuccess={checkSubscription} />
        </Elements>
      )}
    </div>
  );
}
